import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import IcomoonReact from 'icomoon-react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Formik } from 'formik';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import Text from '../../../shared/components/Text/Text';
import { loadSavedProgram } from '../ducks/calendarActions';
import { showErrorToast } from '../../../shared/utils/toastManager';

const CloseButton = styled('button')`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9E9E9E;
  transition: color 0.2s ease;

  &:hover {
    color: #666;
  }
`;

const ModalContent = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px;
  gap: 16px;
  position: relative;
  font-family: 'Nunito Sans';
`;

const Title = styled('h1')`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  max-width: calc(100% - 40px); /* Account for close button */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubText = styled(Text)`
  color: #666;
  margin-top: -16px;
`;

const TwoColumnContainer = styled('div')`
  display: flex;
  gap: 24px;
  width: 100%;
`;

const LeftColumn = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

const RightColumn = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const DatePickerWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;

  .react-datepicker {
    border: none;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    font-family: 'Nunito Sans';
    width: 320px;
  }

  .react-datepicker__header {
    background: white;
    border-bottom: none;
    padding: 15px;
  }

  .react-datepicker__month-container {
    padding: 15px;
    font-family: 'Nunito Sans';
    font-size: 15px;
  }

  .react-datepicker__day-names,
  .react-datepicker__week {
    margin-bottom: 15px;
  }

  .react-datepicker__day--outside-month {
    color: #BDBDBD;
  }

  .react-datepicker__day--inside-month {
    color: #444444;
  }

  .react-datepicker__day--selected {
    background-color: #FF6600;
    color: white;
  }

  .react-datepicker__day--keyboard-selected {
    background: none;
    color: #444444;
  }

  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle,
  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::before {
    border-bottom-color: white;
  }

  .react-datepicker-popper {
    z-index: 1;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  height: 40px;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  padding: 0 12px;
  font-size: 14px;
  color: #333;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  background: white;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${(props) => (props.disabled ? '#E0E0E0' : '#BDBDBD')};
  }

  &:focus {
    outline: none;
    border-color: #BDBDBD;
  }
`;

const ActiveDaysText = styled(Text)`
  color: #666;
  font-size: 18px;
  font-weight: 600;
  margin: 8px 0;
`;

const DescriptionContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DescriptionDisplay = styled('div')`
  width: 100%;
  min-height: 250px;
  padding: 12px;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  font-size: 14px;
  background-color: #F9F9F9;
  color: #666;
  line-height: 1.5;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  flex: 1;
`;

const CheckboxContainer = styled('div')`
  display: flex;
  gap: 12px;
  flex-direction: column;
`;

const CheckboxWrapper = styled('label')`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const Checkbox = styled('input')`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const ButtonContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const Button = styled('button')`
  flex: 1;
  padding: 8px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${(props) => (props.primary ? `
    background: #333;
    color: white;
    border: none;
    &:hover {
      background: #444;
    }
  ` : `
    background: white;
    color: #333;
    border: 2px solid #333;
    &:hover {
      background: #F5F5F5;
    }
  `)}
`;

const EndDateTitleWrapper = styled('div')`
  position: relative;
  display: inline-block;
  cursor: help;
`;

const LoadProgramsModal = ({ closeModal }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedSavedWorkout = useSelector((state) => state.calendar.data.selectedSavedWorkout);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);

  // Helper functions to handle both data structures
  const getWorkoutId = () => selectedSavedWorkout?.info?.id || selectedSavedWorkout?.id;
  const getWorkoutName = () => selectedSavedWorkout?.info?.programName
    || selectedSavedWorkout?.programName;
  const getWorkoutDescription = () => selectedSavedWorkout?.info?.description
    || selectedSavedWorkout?.description;
  const getNumberOfDays = () => selectedSavedWorkout?.info?.numberOfDays
    || selectedSavedWorkout?.numberOfDays;

  const endDateTooltipId = 'end-date-tooltip';

  const initialValues = {
    // moment() (not the native Date constructor) parses a plain 'YYYY-MM-DD' string as
    // local midnight - the native constructor treats it as UTC midnight, which formats
    // back a day early in any timezone behind UTC.
    startDate: selectedDate ? moment(selectedDate).toDate() : new Date(),
    includeTitles: true,
    includeLoads: true,
    replaceWorkouts: false,
  };

  const handleLoadProgram = (values) => {
    if (programType !== 'calendar') {
      // Stub branch — preserved from prior behavior; no dispatch.
      // TODO: Implement endpoint integration for loading non-date-based workouts.
      return;
    }

    if (!getWorkoutId()) {
      showErrorToast('No program selected. Please select a program to load.');
      return;
    }

    if (!selectedReduxCalendar?.id && !selectedReduxAthlete?.id) {
      showErrorToast('Please select a calendar or user to load the program to');
      return;
    }

    const submissionObject = {
      clearExisting: values.replaceWorkouts,
      includePrescribedLoads: values.includeLoads,
      includeWorkoutInfo: values.includeTitles,
      programId: getWorkoutId(),
      startDate: moment(values.startDate).format('YYYY-MM-DD'),
    };

    if (Object.keys(selectedReduxAthlete).length) {
      submissionObject.userId = selectedReduxAthlete.id;
    } else if (Object.keys(selectedReduxCalendar).length) {
      submissionObject.calendarId = selectedReduxCalendar.id;
    }

    dispatch(loadSavedProgram(currentUser.accountCode, submissionObject));
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleLoadProgram}
      render={({ values, setFieldValue, handleSubmit }) => {
        const numDays = getNumberOfDays() || 0;
        const derivedEndDate = moment(values.startDate).add(numDays, 'days').toDate();
        const startDateAsDate = moment.isMoment(values.startDate)
          ? values.startDate.toDate()
          : values.startDate;

        return (
          <ModalContent>
            <CloseButton onClick={closeModal}>
              <IcomoonReact
                iconSet={iconSet}
                size={14}
                icon='remove'
              />
            </CloseButton>
            <Title>
              {getWorkoutName()
                ? `Load ${getWorkoutName()}`
                : 'Load Program'}
            </Title>
            <SubText>
              The start and end date for this program have been selected based on the chosen start date
              and the number of total days in the program.
            </SubText>
            <TwoColumnContainer>
              <LeftColumn>
                <DatePickerWrapper>
                  <Text fontWeight={600}>Start Date</Text>
                  <StyledDatePicker
                    selected={startDateAsDate}
                    onChange={(date) => setFieldValue('startDate', date)}
                    placeholderText='Select Date'
                    dateFormat='MMM dd, yyyy'
                  />
                </DatePickerWrapper>

                <ActiveDaysText>
                  Active Days:
                  {' '}
                  {moment(derivedEndDate).diff(moment(startDateAsDate), 'days') + 1}
                </ActiveDaysText>

                <DatePickerWrapper>
                  <EndDateTitleWrapper>
                    <div
                      style={{ display: 'inline-block' }}
                      data-tooltip-id={endDateTooltipId}
                    >
                      <Text fontWeight={600}>Program to End On:</Text>
                    </div>
                  </EndDateTitleWrapper>
                  <StyledDatePicker
                    selected={derivedEndDate}
                    placeholderText='Select Date'
                    dateFormat='MMM dd, yyyy'
                    disabled
                  />
                </DatePickerWrapper>

                <CheckboxContainer>
                  <CheckboxWrapper>
                    <Checkbox
                      type='checkbox'
                      checked={values.includeTitles}
                      onChange={(e) => setFieldValue('includeTitles', e.target.checked)}
                    />
                    <Text>Include workout titles and colors</Text>
                  </CheckboxWrapper>
                  <CheckboxWrapper>
                    <Checkbox
                      type='checkbox'
                      checked={values.includeLoads}
                      onChange={(e) => setFieldValue('includeLoads', e.target.checked)}
                    />
                    <Text>Include prescribed loads</Text>
                  </CheckboxWrapper>
                  <CheckboxWrapper>
                    <Checkbox
                      type='checkbox'
                      checked={values.replaceWorkouts}
                      onChange={(e) => setFieldValue('replaceWorkouts', e.target.checked)}
                    />
                    <Text>Replace workouts for all selected dates</Text>
                  </CheckboxWrapper>
                </CheckboxContainer>
              </LeftColumn>

              <RightColumn>
                <DescriptionContainer>
                  <Text fontWeight={600}>Description</Text>
                  <DescriptionDisplay>
                    {getWorkoutDescription() || ''}
                  </DescriptionDisplay>
                </DescriptionContainer>
              </RightColumn>
            </TwoColumnContainer>

            <ButtonContainer>
              <Button type='button' primary onClick={() => handleSubmit()}>Load Program</Button>
              <Button type='button' onClick={closeModal}>CANCEL</Button>
            </ButtonContainer>

            {/* ReactTooltip component */}
            <ReactTooltip
              id={endDateTooltipId}
              place='top'
              content='End date is based on start date and active days'
              style={{
                backgroundColor: '#A6A6A6',
                color: 'white',
                fontFamily: 'Nunito Sans',
                fontSize: '13px',
              }}
            />
          </ModalContent>
        );
      }}
    />
  );
};

LoadProgramsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default LoadProgramsModal;
